import { CURRENCY_OPTIONS } from 'projectConstants';
import { FormatProperties } from 'models/response/modelOutput.interface';

export const getSmartDecimalPlaces = (value: unknown): 0 | 1 | 2 => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  // Treat tiny floating point noise as integer.
  const roundedInt = Math.round(numericValue);
  if (Math.abs(numericValue - roundedInt) < 1e-9) return 0;
  // "0.08 form" → show 2 decimals for absolute values < 1 (excluding integers like 0).
  if (Math.abs(numericValue) < 1) return 2;
  // Default for non-integers >= 1.
  return 1;
};

/**
 * `measure` arrives as a currency code ("USD", "INR"), but values are meant to
 * read as symbols. Only a known code is translated: "%", an already-resolved
 * symbol, or any custom unit is returned untouched — `getCurrencySymbol` can't
 * be used directly here because it falls back to "$" for anything it doesn't
 * recognise, which would turn "₹" into "$".
 */
export const resolveMeasureSymbol = <T extends string | null | undefined>(measure: T): T => {
  if (!measure || measure === '%') return measure;
  return (CURRENCY_OPTIONS.find((option) => option.value === measure)?.symbol ?? measure) as T;
};

export const withCurrencySymbol = (
  formatProperties?: FormatProperties,
): FormatProperties | undefined => {
  if (!formatProperties?.measure) return formatProperties;
  return { ...formatProperties, measure: resolveMeasureSymbol(formatProperties.measure) };
};

const formatValue = (totalValues: any, formatProperties: FormatProperties) => {
  const {
    measure: rawMeasure,
    negative_parentheses: negativeParentheses,
    scale,
    thousand_separators: thousandSeparators,
    decimal_points: decimalPoints = 2,
  } = formatProperties;
  // Every branch below prints `measure` verbatim, so resolve it once here —
  // that way direct formatValue callers show "$" rather than "USD" too.
  const measure = resolveMeasureSymbol(rawMeasure);
  // Respect the indicator's configured decimal places; default to 2 if not set.
  // `totalValues` is normally a number or an array to sum, but the API can
  // also return it as a numeric string for some indicators — `.reduce` on a
  // string threw `totalValues.reduce is not a function` and crashed the
  // whole chart with no error boundary above it.
  const rawTotal =
    typeof totalValues === 'number'
      ? totalValues
      : Array.isArray(totalValues)
      ? totalValues.reduce((accumulator: any, item: any) => {
          accumulator += Number(item);
          return accumulator;
        }, 0)
      : typeof totalValues === 'string' && totalValues.trim() !== ''
      ? Number(totalValues.replace(/,/g, ''))
      : totalValues;
  // Guards against anything that isn't a number/array/numeric-string
  // (null, undefined, an object, an empty string, or a malformed numeric
  // string) — without this, that case fell through to the arithmetic below
  // as-is and produced "NaN"/"NaNk" in the UI instead of a clear failure.
  const total = Number.isFinite(rawTotal) ? rawTotal : 0;

  if (total < 0 && negativeParentheses) {
    if (scale === 'thousands') {
      if (thousandSeparators) {
        if (measure) {
          if (measure !== '%') {
            return `(${measure}${((total * -1) / 1000).toLocaleString('en-GB', {
              minimumFractionDigits: decimalPoints,
              maximumFractionDigits: decimalPoints,
            })}k)`;
          }

          return `(${(total / 1000).toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}k${measure})`;
        }

        // measure === NULL
        return `(${(total / 1000).toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}k)`;
      }

      // thousandSeparators === false
      return !measure
        ? `(${(total / 1000).toFixed(decimalPoints)}k)`
        : measure !== '%'
        ? `(${measure}${(total / 1000).toFixed(decimalPoints)}k)`
        : `(${(total / 1000).toFixed(decimalPoints)}k${measure})`;
    }

    if (scale === 'millions') {
      if (thousandSeparators) {
        if (measure) {
          if (measure !== '%') {
            return `(${measure}${((-1 * total) / 1_000_000).toLocaleString('en-GB', {
              minimumFractionDigits: decimalPoints,
              maximumFractionDigits: decimalPoints,
            })}mn)`;
          }

          return `(${(total / 1_000_000).toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}mn${measure})`;
        }

        // measure === NULL
        return `(${(total / 1_000_000).toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}mn)`;
      }

      // thousandSeparators === false
      return !measure
        ? `(${(total / 1_000_000).toFixed(decimalPoints)}mn)`
        : measure !== '%'
        ? `(${measure}${((-1 * total) / 1_000_000).toFixed(decimalPoints)}mn)`
        : `(${(total / 1_000_000).toFixed(decimalPoints)}mn${measure})`;
    }

    if (scale === 'billions') {
      if (thousandSeparators) {
        if (measure) {
          if (measure !== '%') {
            return `(${measure}${((-1 * total) / 1_000_000_000).toLocaleString('en-GB', {
              minimumFractionDigits: decimalPoints,
              maximumFractionDigits: decimalPoints,
            })}bn)`;
          }

          return `(${(total / 1_000_000_000).toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}bn${measure})`;
        }

        // measure === NULL
        return `(${(total / 1_000_000_000).toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}bn)`;
      }

      // thousandSeparators === false
      return !measure
        ? `(${(total / 1_000_000_000).toFixed(decimalPoints)}bn)`
        : measure !== '%'
        ? `(${measure}${((-1 * total) / 1_000_000_000).toFixed(decimalPoints)}bn)`
        : `(${(total / 1_000_000_000).toFixed(decimalPoints)}bn${measure})`;
    }

    if (!scale) {
      if (thousandSeparators) {
        if (measure) {
          if (measure !== '%') {
            return `(${measure}${(-1 * total).toLocaleString('en-GB', {
              minimumFractionDigits: decimalPoints,
              maximumFractionDigits: decimalPoints,
            })})`;
          }

          return `(${total.toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}${measure})`;
        }

        // measure === NULL
        return `(${total.toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })})`;
      }

      // thousandSeparators === false
      return !measure
        ? `(${total.toFixed(decimalPoints)})`
        : measure !== '%'
        ? `(${measure}${(-1 * total).toFixed(decimalPoints)})`
        : `(${total.toFixed(decimalPoints)}${measure})`;
    }
  }

  if (total < 0 && !negativeParentheses) {
    if (scale === 'thousands') {
      if (thousandSeparators) {
        if (measure) {
          if (measure !== '%') {
            return `${measure}${(total / 1000).toLocaleString('en-GB', {
              minimumFractionDigits: decimalPoints,
              maximumFractionDigits: decimalPoints,
            })}k`;
          }

          return `${(total / 1000).toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}k${measure}`;
        }

        // measure === NULL
        return `${(total / 1000).toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}k`;
      }

      // thousandSeparators === false
      return !measure
        ? `${(total / 1000).toFixed(decimalPoints)}k`
        : measure !== '%'
        ? `${measure}${(total / 1000).toFixed(decimalPoints)}k`
        : `${(total / 1000).toFixed(decimalPoints)}k${measure}`;
    }

    if (scale === 'millions') {
      if (thousandSeparators) {
        if (measure) {
          if (measure !== '%') {
            return `${measure}${(total / 1_000_000).toLocaleString('en-GB', {
              minimumFractionDigits: decimalPoints,
              maximumFractionDigits: decimalPoints,
            })}mn`;
          }

          return `${(total / 1_000_000).toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}mn${measure}`;
        }

        // measure === NULL
        return `${(total / 1_000_000).toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}mn`;
      }

      // thousandSeparators === false
      return !measure
        ? `${(total / 1_000_000).toFixed(decimalPoints)}mn`
        : measure !== '%'
        ? `${measure}${(total / 1_000_000).toFixed(decimalPoints)}mn`
        : `${(total / 1_000_000).toFixed(decimalPoints)}mn${measure}`;
    }

    if (scale === 'billions') {
      if (thousandSeparators) {
        if (measure) {
          if (measure !== '%') {
            return `${measure}${(total / 1_000_000_000).toLocaleString('en-GB', {
              minimumFractionDigits: decimalPoints,
              maximumFractionDigits: decimalPoints,
            })}bn`;
          }

          return `${(total / 1_000_000_000).toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}bn${measure}`;
        }

        // measure === NULL
        return `${(total / 1_000_000_000).toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}bn`;
      }

      // thousandSeparators === false
      return !measure
        ? `${(total / 1_000_000_000).toFixed(decimalPoints)}bn`
        : measure !== '%'
        ? `${measure}${(total / 1_000_000_000).toFixed(decimalPoints)}bn`
        : `${(total / 1_000_000_000).toFixed(decimalPoints)}bn${measure}`;
    }

    if (!scale) {
      if (thousandSeparators) {
        if (measure) {
          if (measure !== '%') {
            return `${measure}${total.toLocaleString('en-GB', {
              minimumFractionDigits: decimalPoints,
              maximumFractionDigits: decimalPoints,
            })}`;
          }

          return `${total.toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}${measure}`;
        }

        // measure === NULL
        return `${total.toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}`;
      }

      // thousandSeparators === false
      return !measure
        ? `${total.toFixed(decimalPoints)}`
        : measure !== '%'
        ? `${measure}${total.toFixed(decimalPoints)}`
        : `${total.toFixed(decimalPoints)}${measure}`;
    }
  }

  if (scale === 'thousands') {
    if (thousandSeparators) {
      if (measure) {
        if (measure !== '%') {
          return `${measure}${(total / 1000).toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}k`;
        }

        return `${(total / 1000).toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}k${measure}`;
      }

      // measure === NULL
      return `${(total / 1000).toLocaleString('en-GB', {
        minimumFractionDigits: decimalPoints,
        maximumFractionDigits: decimalPoints,
      })}k`;
    }

    // thousandSeparators === false
    return !measure
      ? `${(total / 1000).toFixed(decimalPoints)}k`
      : measure !== '%'
      ? `${measure}${(total / 1000).toFixed(decimalPoints)}k`
      : `${(total / 1000).toFixed(decimalPoints)}k${measure}`;
  }

  if (scale === 'millions') {
    if (thousandSeparators) {
      if (measure) {
        if (measure !== '%') {
          return `${measure}${(total / 1_000_000).toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}mn`;
        }

        return `${(total / 1_000_000).toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}mn${measure}`;
      }

      // measure === NULL
      return `${(total / 1_000_000).toLocaleString('en-GB', {
        minimumFractionDigits: decimalPoints,
        maximumFractionDigits: decimalPoints,
      })}mn`;
    }

    // thousandSeparators === false
    return !measure
      ? `${(total / 1_000_000).toFixed(decimalPoints)}mn`
      : measure !== '%'
      ? `${measure}${(total / 1_000_000).toFixed(decimalPoints)}mn`
      : `${(total / 1_000_000).toFixed(decimalPoints)}mn${measure}`;
  }

  if (scale === 'billions') {
    if (thousandSeparators) {
      if (measure) {
        if (measure !== '%') {
          return `${measure}${(total / 1_000_000_000).toLocaleString('en-GB', {
            minimumFractionDigits: decimalPoints,
            maximumFractionDigits: decimalPoints,
          })}bn`;
        }

        return `${(total / 1_000_000_000).toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}bn${measure}`;
      }

      // measure === NULL
      return `${(total / 1_000_000_000).toLocaleString('en-GB', {
        minimumFractionDigits: decimalPoints,
        maximumFractionDigits: decimalPoints,
      })}bn`;
    }

    // thousandSeparators === false
    return !measure
      ? `${(total / 1_000_000_000).toFixed(decimalPoints)}bn`
      : measure !== '%'
      ? `${measure}${(total / 1_000_000_000).toFixed(decimalPoints)}bn`
      : `${(total / 1_000_000_000).toFixed(decimalPoints)}bn${measure}`;
  }

  if (thousandSeparators) {
    if (measure) {
      if (measure !== '%') {
        return `${measure}${total?.toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })}`;
      }

      return `${total?.toLocaleString('en-GB', {
        minimumFractionDigits: decimalPoints,
        maximumFractionDigits: decimalPoints,
      })}${measure}`;
    }

    // measure === NULL
    return total
      ? total?.toLocaleString('en-GB', {
          minimumFractionDigits: decimalPoints,
          maximumFractionDigits: decimalPoints,
        })
      : '0';
  }

  // thousandSeparators === false
  return !measure
    ? total?.toFixed(decimalPoints)
    : measure !== '%'
    ? `${measure}${total?.toFixed(decimalPoints)}`
    : `${total?.toFixed(decimalPoints)}${measure}`;
};

export default formatValue;

/**
 * Format a numeric indicator value using its indicator type and format properties.
 *
 * Handles ratio / percentage indicators (returns e.g. "35.5%") and delegates all
 * other indicator types to `formatValue` which applies scale, measure prefix/suffix,
 * thousand separators and the configured decimal places.
 *
 * This is the single shared formatter used by Table rows, Variance rows and KPI cards
 * so that number display is consistent across all instrument types.
 */
export const getDataTypeBasedValuewithNewValue = (
  type: string,
  value: any,
  formatProperties: any,
): string => {
  const normalizedFormatProperties = withCurrencySymbol(formatProperties) ?? formatProperties;
  const smartDecimalPoints =
    normalizedFormatProperties?.measure === '%'
      ? normalizedFormatProperties?.decimal_points
      : getSmartDecimalPlaces(value);
  const smartFormatProperties = {
    ...normalizedFormatProperties,
    // Dashboard requirement: integers -> 0dp, values < 1 -> 2dp, other decimals -> 1dp
    // Apply only for non-percent measures.
    ...(normalizedFormatProperties?.measure === '%' ? {} : { decimal_points: smartDecimalPoints }),
  };

  const formattedValue = formatValue(value, smartFormatProperties);

  if (type && (type.toLowerCase() === 'ratio' || type.toLowerCase() === 'percentage')) {
    // Ratio / percentage values come from the engine as decimal fractions (e.g. 0.355 → 35.5%).
    const numericValue = Number.parseFloat(String(value).replace('%', ''));
    if (!Number.isFinite(numericValue)) return formattedValue;
    // const displayPercent = numericValue * 100;
    const decimalPlaces =
      formatProperties?.decimal_points ?? getSmartDecimalPlaces(numericValue * 100);
    return `${(numericValue * 100).toFixed(decimalPlaces)}%`;
  }

  return formattedValue;
};
